import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { safeJsonParse } from '../../../utils/safeJsonParse';
import { parseTabTags, tabHasTag } from '../../../utils/parseTabTags';
import { formatFilterText } from '../../../utils/formatFilterText';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styles from './index.module.scss';
import {
  setCurrentTab,
  setCurrentTabName,
  setSkipFilterReset,
  resetFilters,
} from '../../../redux/slices/currentTabSlice';
import { setFilterBValueWithUpdate } from '../../../redux/actions/filterActions';
import { setQuote } from '../../../redux/slices/quoteSlice';
import { setTabOrPublicationPage } from '../../../redux/slices/appSlice';
import { setPaginationPage } from '../../../redux/slices/paginationSlice';
import {
  setMobileMenuOpen,
  openMobileMenuInFiltersMode,
} from '../../../redux/slices/mobileMenuSlice';
import { selectCartItemsCount } from '../../../redux/slices/cartSlice';
import HeaderLogo from '../../render_components/other_components/header_logo';
import DynamicPodheader from '../podheader_system/dynamic_podheader';
import MobilePodheader from '../podheader_system/mobile_podheader';

const HEADER_TEMPLATE_NAMES = ['header_logo'];

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tabsData = useSelector((state) => state.tabs_data.tabs);
  const others = useSelector((state) => state.others_data.others);
  const showName = useSelector((state) => state.currentTab.show_name);
  const tabOrPublicationPage = useSelector((state) => state.app.tab_or_publication_page);
  const isMobileMenuOpen = useSelector((state) => state.mobileMenu.isMobileMenuOpen);
  const cartItemsCount = useSelector(selectCartItemsCount);

  const [isMobile, setIsMobile] = useState(false);
  const [isDynamicPodheaderVisible, setIsDynamicPodheaderVisible] = useState(false);
  const [isDynamicPodheaderClosing, setIsDynamicPodheaderClosing] = useState(false);
  const [isScrolledDown, setIsScrolledDown] = useState(false);

  const [headerTabs, setHeaderTabs] = React.useState([]);
  const [hoveredDropdown, setHoveredDropdown] = React.useState(null);
  const dropdownTimeoutRef = React.useRef(null);
  const [othersToRender, setOthersToRender] = React.useState([]);

  // Явный рендер other-компонентов: перебор others по template_name
  useEffect(() => {
    if (!others || !Array.isArray(others)) {
      setOthersToRender([]);
      return;
    }
    const allowed = new Set(HEADER_TEMPLATE_NAMES);
    const list = [];
    others.forEach((el) => {
      const template = safeJsonParse(el.template, {});
      const name = template?.template_name;
      if (name && allowed.has(name)) {
        list.push({
          template_name: name,
          contentData: safeJsonParse(el.content, []),
          path_to_folder:
            el.path && el.blank_name ? 'https://' + el.path + el.blank_name + '/' : null,
        });
      }
    });
    setOthersToRender(list);
  }, [others]);

  // Получаем все данные вкладок для поиска фильтров других разделов
  const allTabsData = useSelector((state) => state.tabs_data.tabs) || [];

  // Функция для получения фильтров конкретного раздела
  const getFiltersForSection = (sectionName) => {
    const sectionTab = allTabsData.find((tab) => tab.eng_name === sectionName);
    if (sectionTab && sectionTab.publications) {
      // Обрабатываем публикации для получения фильтров (аналогично currentTabSlice)
      let allFilterBValues = ['все']; // Начинаем с 'все'

      sectionTab.publications.forEach((publication) => {
        if (publication.publication_settings) {
          const isJsonString =
            publication.publication_settings.trim().startsWith('{') ||
            publication.publication_settings.trim().startsWith('[');

          if (isJsonString) {
            try {
              const obj = safeJsonParse(publication.publication_settings, {});

              if (obj.publication_filter_b) {
                const filterBValues = Array.isArray(obj.publication_filter_b)
                  ? obj.publication_filter_b
                  : [obj.publication_filter_b];

                // Добавляем все значения к общему массиву
                allFilterBValues = [...allFilterBValues, ...filterBValues];
              }
            } catch (error) {
              // Игнорируем ошибки парсинга
            }
          }
        }
      });

      // Убираем дубликаты и пустые значения, исключаем 'все' из выпадающего меню
      const uniqueFilterB = [
        ...new Set(
          allFilterBValues.filter(
            (item) => item !== null && item !== undefined && item !== '' && item !== 'все',
          ),
        ),
      ];

      return uniqueFilterB;
    }
    return [];
  };

  useEffect(() => {
    if (others && Array.isArray(others)) {
      const quoteData = others.find((el) => el.tab_tag === 'quote');
      if (
        quoteData &&
        quoteData.content &&
        quoteData.content[0] &&
        quoteData.content[0].block_content
      ) {
        dispatch(setQuote(quoteData.content[0].block_content));
      }
    }
  }, [others, dispatch]);

  React.useEffect(() => {}, [showName]);

  // Определение мобильного режима
  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Отслеживание скролла страницы
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrolledDown = scrollTop > 50;

      setIsScrolledDown(scrolledDown);

      // Если подхедер открыт и страница прокручена в самый верх - скрываем без анимации
      if (isDynamicPodheaderVisible && scrollTop <= 10) {
        setIsDynamicPodheaderVisible(false);
        setIsDynamicPodheaderClosing(false);
      }
    };

    // Добавляем слушатель события скролла
    window.addEventListener('scroll', handleScroll);

    // Очищаем слушатель при размонтировании компонента
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Очистка таймера при размонтировании
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [isDynamicPodheaderVisible]);
  React.useEffect(() => {
    if (tabsData && tabsData.length > 0) {
      const processed = tabsData
        .map((tab, index) => {
          const tagArray = parseTabTags(tab.tab_tag);
          return {
            ...tab,
            tagArray,
            firstTag: tagArray.length > 0 ? tagArray[0] : null,
          };
        })
        .filter(
          (tab) =>
            tab.tagArray.includes('header') ||
            tab.tagArray.includes('header_list') ||
            tab.tagArray.includes('header_cart'),
        );
      setHeaderTabs(processed);
    }
  }, [tabsData]);

  // Находим вкладку с тегом "main_header"
  const mainHeaderTab = tabsData?.find((tab) => {
    if (tab.tab_tag) {
      return tabHasTag(tab.tab_tag, 'main_header');
    }
    return false;
  });

  // Определяем текущий раздел из URL (аналогично main компоненту)
  const currentPath = location.pathname;
  const currentSection = currentPath === '/' ? 'main_tab' : currentPath.split('/')[1];

  // Находим данные для sh_podheader компонента по тегам для текущего раздела
  const shPodheaderData =
    tabsData && Array.isArray(tabsData)
      ? tabsData.find((el) => {
          if (el.tab_tag && el.eng_name === currentSection) {
            return tabHasTag(el.tab_tag, 'sh_podheader');
          }
          return false;
        })
      : null;

  // Временное логирование для отладки

  // Детальное логирование tabsData для отладки
  if (tabsData && Array.isArray(tabsData)) {
    tabsData.forEach((tab, index) => {
      //   eng_name: tab.eng_name,
      //   tab_tag: tab.tab_tag,
      //   hasShPodheaderTag: tab.tab_tag && tab.tab_tag.includes('sh_podheader'),
      //   matchesCurrentSection: tab.eng_name === currentSection,
      // });
    });
  }

  const handleLogoClick = () => {
    // Переходим на страницу из mainHeaderTab только если он найден
    if (mainHeaderTab) {
      // Определяем путь для навигации
      let path = '/unknown';
      if (mainHeaderTab.eng_name === 'main_tab') {
        path = '/';
      } else {
        path = '/' + mainHeaderTab.eng_name;
      }

      // Сбрасываем фильтры для обычной навигации
      dispatch(resetFilters());

      // Переходим на страницу
      navigate(path);
      dispatch(setTabOrPublicationPage('tab'));
      dispatch(setCurrentTab(mainHeaderTab));
      // Сбрасываем пагинацию на первую страницу
      dispatch(setPaginationPage(1));
    }
    // Если mainHeaderTab не найден, ничего не делаем
  };

  const handleToggleDynamicPodheader = () => {
    setIsDynamicPodheaderVisible(!isDynamicPodheaderVisible);
  };

  const handleCloseDynamicPodheader = () => {
    setIsDynamicPodheaderClosing(true);
    // После завершения анимации закрытия скрываем компонент
    setTimeout(() => {
      setIsDynamicPodheaderVisible(false);
      setIsDynamicPodheaderClosing(false);
    }, 300); // Длительность анимации должна совпадать с CSS
  };

  // Обработчик клика на элемент выпадающего меню
  const handleDropdownItemClick = (sectionName, filterValue) => {
    // Находим раздел по имени
    const targetSection = tabsData.find((tab) => tab.eng_name === sectionName);

    if (targetSection) {
      // Сначала устанавливаем флаг, чтобы пропустить сброс фильтров при смене раздела
      dispatch(setSkipFilterReset(true));

      // Сначала устанавливаем выбранный фильтр в Redux
      dispatch(setFilterBValueWithUpdate(filterValue));

      // Небольшая задержка, чтобы фильтр успел установиться
      setTimeout(() => {
        // Переходим в раздел
        const path = `/${targetSection.eng_name}`;
        navigate(path);

        // Обновляем Redux состояние
        dispatch(setCurrentTab(targetSection));
        dispatch(setCurrentTabName(targetSection.eng_name));
        dispatch(setTabOrPublicationPage('tab'));

        // Сбрасываем флаг после еще одной задержки
        setTimeout(() => {
          dispatch(setSkipFilterReset(false));
        }, 200);
      }, 50);
    }

    // Закрываем выпадающее меню
    setHoveredDropdown(null);
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        {othersToRender.length === 0 ? (
          <HeaderLogo contentData={[]} path_to_folder={null} onClick={handleLogoClick} />
        ) : (
          othersToRender.map((item, idx) => {
            switch (item.template_name) {
              case 'header_logo':
                return (
                  <HeaderLogo
                    key={idx}
                    contentData={item.contentData}
                    path_to_folder={item.path_to_folder}
                    onClick={handleLogoClick}
                  />
                );
              default:
                return null;
            }
          })
        )}

        {headerTabs.length > 0 && (
          <nav className={styles.navigation}>
            {isMobile ? (
              <div className={styles.mobileButtonsContainer}>
                {shPodheaderData && (
                  <button
                    className={styles.mobileSearchButton}
                    onClick={() => dispatch(openMobileMenuInFiltersMode())}
                    aria-label='Открыть поиск и фильтры'>
                    ⌕
                  </button>
                )}
                <button
                  className={styles.mobileMenuButton}
                  onClick={() => dispatch(setMobileMenuOpen(true))}
                  aria-label='Открыть меню'>
                  ☰
                </button>
              </div>
            ) : (
              <ul className={styles.navList}>
                {headerTabs.map((tab, index) => {
                  // Используем tab.eng_name для URL
                  let path = '/unknown';
                  if (tab.eng_name == 'main_tab') {
                    path = '/';
                  } else {
                    path = '/' + tab.eng_name;
                  }

                  const handleTabClick = () => {
                    // Сбрасываем фильтры для обычной навигации
                    dispatch(resetFilters());

                    // При клике на раздел всегда меняем showName
                    dispatch(setTabOrPublicationPage('tab'));
                    dispatch(setCurrentTab(tab));
                  };

                  const isActive = showName === tab.show_name;
                  const hasHeaderList = tab.tagArray.includes('header_list');
                  const hasHeaderCart = tab.tagArray.includes('header_cart');

                  // Получаем фильтры для конкретного раздела
                  const sectionFilters = hasHeaderList ? getFiltersForSection(tab.eng_name) : [];

                  // Показываем выпадающее меню только если есть header_list И больше 1 фильтра
                  const shouldShowDropdown = hasHeaderList && sectionFilters.length > 1;

                  // Показываем меню при наведении на стрелочку
                  const isDropdownOpen = hoveredDropdown === index;

                  return (
                    <React.Fragment key={index}>
                      <li className={styles.navItem}>
                        <Link
                          onClick={handleTabClick}
                          to={path}
                          className={`${styles.navLink} ${isActive ? styles.active : ''}`}>
                          <div
                            className={`${styles.navText} ${hasHeaderCart && cartItemsCount > 0 ? styles.cartText : ''}`}>
                            {(tab.show_name || tab.tab_name || `Раздел ${index + 1}`).toLowerCase()}
                            {hasHeaderCart && cartItemsCount > 0 && (
                              <span className={styles.cartBadge}>{cartItemsCount}</span>
                            )}
                          </div>
                          {shouldShowDropdown && (
                            <div
                              className={styles.arrowContainer}
                              onMouseEnter={() => {
                                // Очищаем предыдущий таймер
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current);
                                  dropdownTimeoutRef.current = null;
                                }
                                setHoveredDropdown(index);
                              }}
                              onMouseLeave={() => {
                                // Очищаем предыдущий таймер
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current);
                                }
                                // Добавляем задержку перед закрытием
                                dropdownTimeoutRef.current = setTimeout(() => {
                                  setHoveredDropdown(null);
                                }, 150);
                              }}>
                              <span className={styles.arrow}>▼</span>
                            </div>
                          )}
                        </Link>

                        {shouldShowDropdown && isDropdownOpen && (
                          <div
                            className={styles.dropdown}
                            onMouseEnter={() => {
                              // Отменяем таймер закрытия при наведении на меню
                              if (dropdownTimeoutRef.current) {
                                clearTimeout(dropdownTimeoutRef.current);
                                dropdownTimeoutRef.current = null;
                              }
                              setHoveredDropdown(index);
                            }}
                            onMouseLeave={() => setHoveredDropdown(null)}>
                            {sectionFilters.map((filterValue, filterIndex) => (
                              <div
                                key={filterIndex}
                                className={styles.dropdownItem}
                                onClick={() => handleDropdownItemClick(tab.eng_name, filterValue)}>
                                {formatFilterText(filterValue)}
                              </div>
                            ))}
                          </div>
                        )}
                      </li>
                      {index < headerTabs.length - 1 && <li className={styles.separator}>|</li>}
                    </React.Fragment>
                  );
                })}
              </ul>
            )}
          </nav>
        )}
      </div>

      {/* Кнопка для показа DynamicPodheader - показывается только когда DynamicPodheader скрыт, страница прокручена и не открыта публикация */}
      {shPodheaderData &&
        !isDynamicPodheaderVisible &&
        isScrolledDown &&
        tabOrPublicationPage !== 'publication' && (
          <div className={styles.toggleButtonContainer}>
            <button
              className={styles.toggleButton}
              onClick={handleToggleDynamicPodheader}
              title='Показать дополнительную информацию'>
              ▼
            </button>
          </div>
        )}
      {/* DynamicPodheader компонент при наличии тега sh_podheader - визуально под header, не показываем если открыта публикация */}
      {shPodheaderData && isDynamicPodheaderVisible && tabOrPublicationPage !== 'publication' && (
        <div
          className={`${styles.dynamicPodheaderContainer} ${
            isDynamicPodheaderClosing ? styles.closing : ''
          }`}>
          <DynamicPodheader
            data={safeJsonParse(shPodheaderData.content, {})}
            path={shPodheaderData.path + shPodheaderData.blank_name + '/'}
            onClose={handleCloseDynamicPodheader}
            currentSection={currentSection}
            tag={shPodheaderData.tab_tag}
          />
        </div>
      )}

      {/* Мобильный подхедер */}
      <MobilePodheader
        isOpen={isMobileMenuOpen}
        onClose={() => dispatch(setMobileMenuOpen(false))}
      />
    </header>
  );
};

export default Header;
